import nextId from 'react-id-generator';
import {
  EquipmentModal,
  ScopingEquipmentTableItem,
  ScopingInfo,
  SectorEquipmentDataResp,
  SectorEquipmentResp,
  SectorEquipmentTypeResp,
} from '@models/interfaces';
import { arraySort } from './array-sort';

export const sortEquipments = (data: EquipmentModal[]): EquipmentModal[] =>
  arraySort<EquipmentModal>(data, 'ASC', 'equipmentTypeName', 'modelNumber');

export const getProcessedEquipments = (
  equipmentTypeNames: Set<string>,
  equipments: ScopingEquipmentTableItem[],
  generatedIdName?: string,
): EquipmentModal[] =>
  sortEquipments(
    (equipments || []).map(
      (equipment: ScopingEquipmentTableItem): EquipmentModal => {
        equipmentTypeNames.add(equipment.equipment.equipmentType.name);

        return {
          RAD: equipment?.RAD,
          quantity: equipment.quantity,
          equipment: equipment.equipment.id,
          modelNumber: equipment.equipment.modelNumber,
          equipmentType: equipment.equipment.equipmentType.id,
          equipmentTypeName: equipment.equipment.equipmentType.name,
          ...(equipment.leaseRights
            ? { leaseRights: equipment.leaseRights }
            : {}),
          ...(generatedIdName ? { generatedId: nextId(generatedIdName) } : {}),
        };
      },
    ),
  );

export const equipmentDuplicatesCombiner = (
  data: EquipmentModal[],
): EquipmentModal[] =>
  sortEquipments(
    Object.values(
      data.reduce(
        (acc: Record<string, EquipmentModal>, curItem: EquipmentModal) => {
          const key = `${curItem.equipmentType}${curItem.equipment}`;

          return {
            ...acc,
            [key]: {
              ...curItem,
              quantity: acc[key]
                ? acc[key].quantity + curItem.quantity
                : curItem.quantity,
            },
          };
        },
        {},
      ),
    ),
  );

export const getEquipmentTotalsFromScoping = (
  equipmentTypeNames: Set<string>,
  scopingInfo: ScopingInfo,
  generatedIdName?: string,
): EquipmentModal[] => {
  const scopingData = scopingInfo ?? {};
  const { otherEquipment, scopingSectors } = scopingData;

  const processedOtherEquipments = getProcessedEquipments(
    equipmentTypeNames,
    otherEquipment,
    generatedIdName,
  );

  const allSectorsEquipments = (scopingSectors || []).flatMap(
    (sector: SectorEquipmentDataResp) =>
      sector.sectorEquipmentTypes.reduce(
        (acc: EquipmentModal[], curItem: SectorEquipmentTypeResp) => {
          equipmentTypeNames.add(curItem.equipmentType.name);

          return [
            ...acc,
            ...curItem.equipments.map(
              (equipment: SectorEquipmentResp): EquipmentModal => ({
                equipmentType: curItem.equipmentType.id,
                equipmentTypeName: curItem.equipmentType.name,
                equipment: equipment.equipment?.id || '',
                modelNumber: equipment.equipment?.modelNumber || '',
                quantity: equipment.quantity || 1,
                ...(generatedIdName
                  ? { generatedId: nextId(generatedIdName) }
                  : {}),
              }),
            ),
          ];
        },
        [],
      ),
  );

  return equipmentDuplicatesCombiner([
    ...processedOtherEquipments,
    ...allSectorsEquipments,
  ]);
};
